{
  "title": "How to Create Rounds and Configure Their Schedule",
  "description": "Create preliminary/final rounds, and set each round's registration, submission, judging, and result-publish timeline.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, the competition has one or more rounds, each with a full schedule configured — the system automatically controls when registration, submission, and judging open and close based on these times.",
  "roleNote": "Competition Admin",
  "prerequisites": [
    "The competition has already been created via the request flow and you have admin access"
  ],
  "pathLabel": "Organizer Console → Competition Settings → Rounds",
  "steps": [
    {
      "title": "1. Create a round",
      "description": "Add a round and specify its type (preliminary or final) and name; a competition can have a single round, or a two-round preliminary + final format."
    },
    {
      "title": "2. Set the full schedule chain",
      "description": "Configure, in order: registration start/end, submission start/end, judging start/end, and result publish — 5 timing links in total. The system checks that these are in a sensible order (e.g. submission cannot end before it starts, judging cannot start before submission ends); any individual link can be left blank for \"no limit\". Registration and submission times may overlap; during the overlap the competition status reads \"Registration open\" and participants who have finished registration and team setup can still start or continue their work."
    },
    {
      "title": "3. Bind the submission form and rubric",
      "description": "Assign this round the submission form version and default rubric it should use — judges and participants fill in and score against whichever version is bound to this round."
    },
    {
      "title": "4. Adjust the round's status as needed",
      "description": "A round's status is one of \"Draft / Open / Judging / Closed\" — adjust it manually as things progress, so participants and judges can see which stage the round is in."
    }
  ],
  "afterward": [
    "This round's schedule automatically controls when participants can register and submit, and when judges can start scoring",
    "In a multi-round competition, each round's schedule is set independently and does not affect the others; dates are driven by rounds, and the competition-level dates shown on the Overview are synced automatically to the earliest start and latest end across the rounds, so you do not need to (and should not) edit them separately"
  ],
  "nextSteps": "Once the schedule is set, see \"How to Build and Publish Forms\" to set up the registration and submission forms this round will use.",
  "faq": [
    {
      "q": "Can the submission deadline be later than the judging start time?",
      "a": "No — the schedule chain rule requires the submission end time to not be later than the judging start time (when both are set); saving is rejected and the conflicting fields are flagged."
    },
    {
      "q": "Do I have to set all 5 timing links?",
      "a": "No, each one can be left blank to mean \"unrestricted\"; but the ordering check for a blank link is skipped, so keep an eye on whether the actual behavior still matches what you expect."
    },
    {
      "q": "When do I need to create two rounds?",
      "a": "When the competition advances teams from a preliminary round into a final round, create both a \"Preliminary\" and a \"Final\" round, and use the \"Advance\" feature to decide which teams move from preliminary to final; a single-round competition only needs one round."
    },
    {
      "q": "If I extend one round's registration deadline, do I also need to change the dates on the Overview?",
      "a": "No. Just change that round's registration deadline on the Rounds page; the system syncs the competition-level dates automatically, and the public page, the participant pages and the server-side deadline checks all update consistently."
    }
  ]
}
